import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    Calendar,
    Building,
    ChevronRight,
    FileDown,
    Loader2,
    MapPin,
    Send,
    Smartphone,
    Wallet,
    X,
} from 'lucide-react';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import QRCode from 'qrcode';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { useCustomerAuth } from 'context/CustomerAuthContext';
import { downloadFile } from 'utility/browser';
import { formatEventDateRange } from 'utility/dateFormatting';
import dayjs from 'dayjs';
import { useNotification } from 'utility/notification';
import { createMapsURL } from 'utility/maps';

const AppleWalletEN =
    'https://frontdoorplus.s3.ca-central-1.amazonaws.com/storefront-images/web/apple-wallet-e.png';
const AppleWalletFR =
    'https://frontdoorplus.s3.ca-central-1.amazonaws.com/storefront-images/web/apple-wallet-f.png';
const GoogleWalletEN =
    'https://frontdoorplus.s3.ca-central-1.amazonaws.com/storefront-images/web/google-wallet-e.png';
const GoogleWalletFR =
    'https://frontdoorplus.s3.ca-central-1.amazonaws.com/storefront-images/web/google-wallet-f.png';

export default function SelfServe() {
    return (
        <>
            <SelfServePortal />
        </>
    );
}

// **** Self-Serve Portal **** //

function SelfServePortal() {
    const params = useParams();
    const eventId = params.eventId.replace(/[^0-9]/g, '');

    const [event, tickets, status, error, reload] = useEventAndCustomerTickets(eventId);

    if (status === 'loading') {
        return <LoadingState />;
    }

    if (status === 'error') {
        return (
            <AuthenticationFailedState
                message={
                    error?.message || t`Unable to load your event and tickets. Please try again.`
                }
            />
        );
    }

    return (
        <div className="min-h-[calc(100vh-64px)]">
            <div className="mx-auto w-full max-w-screen-xl px-4 py-8">
                <EventInfo eventId={eventId} event={event} tickets={tickets} />

                <h2 className="mt-12 text-3xl md:text-4xl font-extrabold font-inter text-foreground">
                    <Trans>Your Tickets</Trans>
                </h2>

                <div className="space-y-4">
                    {tickets.map((ticket) => (
                        <Ticket
                            key={ticket.unipass_id}
                            eventId={eventId}
                            ticket={ticket}
                            onTicketShared={reload}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function EventInfo({ eventId, event, tickets }) {
    const { accessToken, customerFetch } = useCustomerAuth();
    const [loading, setLoading] = useState(false);
    const [walletDialogOpen, setWalletDialogOpen] = useState(false);
    const { i18n } = useLingui();
    const { createNotification } = useNotification();

    const dateString = formatEventDateRange(event.start_date, event.end_date, i18n.locale);

    const venueName = event.address1;

    const addressDisplay = [event.address2, event.city, event.prov_state]
        .filter(Boolean)
        .join(', ');

    const addressMapURL = createMapsURL(
        event.address2,
        event.city,
        event.prov_state,
        event.postal_code,
    );

    const ticketCount = tickets.length;

    const scanCount = tickets.reduce((p, n) => p + n.scanned_status, 0);
    const shareCount = tickets.reduce((p, n) => (n.shares.length > 0 ? p + 1 : p), 0);

    const handleDownload = async () => {
        if (loading) return;
        setLoading(true);

        try {
            const headers = {
                'Content-Type': 'application/json',
            };
            const response = await customerFetch(`event/${eventId}/pdf?tz=${dayjs.tz.guess()}`, {
                headers,
            });

            if (response.status !== 200) {
                throw new Error();
            }

            const pdf = await response.text();
            await downloadFile(`tickets.pdf`, pdf);
        } catch {
            createNotification(t`Unable to download tickets`, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="border-solid border-border">
            <CardHeader className="pb-6">
                <CardTitle className="text-xl md:text-2xl">{event.event_title}</CardTitle>
                <Link
                    to={`/host/${event.host_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex self-start items-center gap-2 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                >
                    {event.host_name}
                </Link>
            </CardHeader>
            <CardContent className="flex flex-col md:flex-row gap-6 justify-between">
                <div className="space-y-3">
                    {dateString ? (
                        <div className="flex items-center gap-2">
                            <Calendar
                                size={18}
                                className="text-muted-foreground shrink-0"
                                aria-hidden
                            />
                            <span className="text-sm">{dateString}</span>
                            <Link
                                to={`/event/${eventId}/calendar`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:opacity-60 transition-opacity"
                            >
                                <Trans>Add to Calendar</Trans>
                            </Link>
                        </div>
                    ) : null}
                    {venueName ? (
                        <div className="flex items-center gap-2">
                            <Building
                                size={18}
                                className="text-muted-foreground shrink-0"
                                aria-hidden
                            />
                            <span className="text-sm">{venueName}</span>
                        </div>
                    ) : null}
                    {addressDisplay ? (
                        <div className="flex items-center gap-2">
                            <MapPin
                                size={18}
                                className="text-muted-foreground shrink-0 mt-0.5"
                                aria-hidden
                            />
                            <span className="text-sm text-muted-foreground">{addressDisplay}</span>
                            {addressMapURL ? (
                                <a
                                    href={addressMapURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-primary hover:opacity-60 transition-opacity"
                                >
                                    <Trans>View Map</Trans>
                                </a>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="flex gap-3 items-center pt-4">
                        <p className="text-lg font-semibold inline">{t`${ticketCount} Tickets`}</p>
                        <p className="text-sm text-muted-foreground inline">{t`${scanCount} Checked-In • ${shareCount} Shared`}</p>
                    </div>
                </div>
                <div className="flex flex-col space-y-3 justify-end">
                    {tickets.length <= 10 && (
                        <AddAllToWalletButton
                            appleWalletUrl={event.apple_wallet_url}
                            googleWalletUrl={event.google_wallet_url}
                            onClick={() => setWalletDialogOpen(true)}
                        />
                    )}
                    <Button disabled={loading} onClick={handleDownload}>
                        <Trans>Download All Tickets</Trans>
                    </Button>
                </div>
            </CardContent>
            <WalletDialog
                open={walletDialogOpen}
                onOpenChange={setWalletDialogOpen}
                appleWalletUrl={event.apple_wallet_url}
                googleWalletUrl={event.google_wallet_url}
                locale={i18n.locale}
                accessToken={accessToken}
            />
        </Card>
    );
}

function Ticket({ eventId, ticket, onTicketShared }) {
    const { accessToken, customerFetch } = useCustomerAuth();
    const [loading, setLoading] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [walletDialogOpen, setWalletDialogOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [shareError, setShareError] = useState('');
    const [shareLoading, setShareLoading] = useState(false);
    const { i18n } = useLingui();
    const { createNotification } = useNotification();

    const handleDownload = async () => {
        if (loading) return;
        setLoading(true);

        try {
            const headers = {
                'Content-Type': 'application/json',
            };
            const response = await customerFetch(
                `event/${eventId}/unipass/${ticket.unipass_id}/pdf?tz=${dayjs.tz.guess()}`,
                { headers },
            );

            if (response.status !== 200) {
                throw new Error();
            }

            const pdf = await response.text();
            await downloadFile(
                `tickets-${formatUnipassNumber(ticket.unipass_number, false)}.pdf`,
                pdf,
            );
        } catch {
            createNotification(t`Unable to download tickets`, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleShare = () => {
        setViewDialogOpen(false);
        setRecipientEmail('');
        setShareError('');
        setShareDialogOpen(true);
    };

    const handleWallet = () => {
        setViewDialogOpen(false);
        setWalletDialogOpen(true);
    };

    const handleShareCancel = () => {
        if (shareLoading) return;
        setShareDialogOpen(false);
        setRecipientEmail('');
        setShareError('');
    };

    const handleShareSend = async () => {
        if (shareLoading || !recipientEmail.trim()) return;
        setShareLoading(true);
        setShareError('');

        try {
            const headers = {
                'Content-Type': 'application/json',
            };

            const response = await customerFetch(
                `event/${eventId}/unipass/${ticket.unipass_id}/share?locale=${i18n.locale}`,
                {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ email: recipientEmail.trim() }),
                },
            );

            if (response.status !== 200) {
                if (response.status === 400) {
                    setShareError(t`Ensure the email address is valid`);
                } else if (response.status === 429) {
                    setShareError(t`You've shared too many tickets. Please try again later.`);
                } else {
                    setShareError(t`Unable to share ticket`);
                }

                return;
            }

            createNotification(t`Ticket shared successfully`, { variant: 'success' });
            setShareDialogOpen(false);
            setRecipientEmail('');
            setShareError('');
            onTicketShared();
        } catch {
            setShareError(t`Unable to share ticket`);
        } finally {
            setShareLoading(false);
        }
    };

    const isScanned = Boolean(ticket.scanned_status === 1 && ticket.scan_time);
    const isShared = Array.isArray(ticket.shares) && ticket.shares.length > 0;

    const scanTimeAgo = isScanned ? dayjs(ticket.scan_time).fromNow() : '';

    let statusMessage = '';

    if (isShared) {
        statusMessage = t`Shared with ${ticket.shares[0].recipient_email} ${dayjs(ticket.shares[0].created_at).fromNow()}`;
    } else if (isScanned) {
        statusMessage = t`Checked-in ${scanTimeAgo}`;
    } else {
        statusMessage = t`Ready when you are`;
    }

    const seatMessage = formatAssignedSeat(ticket);

    return (
        <>
            <Card className="border-solid border-border">
                <CardContent className="flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-baseline gap-2">
                            <CardTitle className="text-lg">{ticket.prod_name}</CardTitle>
                            <span className="text-sm text-muted-foreground">
                                {formatUnipassNumber(ticket.unipass_number)}
                            </span>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {isScanned ? (
                                <span className="rounded-full border border-solid border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                                    <Trans>Checked In</Trans> • {scanTimeAgo}
                                </span>
                            ) : null}
                            {isShared ? (
                                <span className="rounded-full border border-solid border-info bg-info/10 px-3 py-1 text-sm font-medium text-info">
                                    <Trans>Sent</Trans>
                                </span>
                            ) : null}
                        </div>

                        {seatMessage ? (
                            <p className="text-sm text-muted-foreground">{seatMessage}</p>
                        ) : null}

                        <p className="text-sm text-muted-foreground">{statusMessage}</p>
                    </div>

                    <Button className="w-full md:w-auto" onClick={() => setViewDialogOpen(true)}>
                        <Trans>Open Ticket</Trans>
                        <ChevronRight aria-hidden />
                    </Button>
                </CardContent>
            </Card>

            <ViewTicketDialog
                open={viewDialogOpen}
                onOpenChange={setViewDialogOpen}
                ticket={ticket}
                onWallet={handleWallet}
                onSend={handleShare}
                onDownload={handleDownload}
                downloadLoading={loading}
            />

            <WalletDialog
                open={walletDialogOpen}
                onOpenChange={setWalletDialogOpen}
                appleWalletUrl={ticket.apple_wallet_url}
                googleWalletUrl={ticket.google_wallet_url}
                locale={i18n.locale}
                accessToken={accessToken}
            />

            <ShareTicketDialog
                open={shareDialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        handleShareCancel();
                        return;
                    }
                    setShareDialogOpen(true);
                }}
                ticket={ticket}
                recipientEmail={recipientEmail}
                onRecipientEmailChange={(email) => {
                    setRecipientEmail(email);
                    setShareError('');
                }}
                onCancel={handleShareCancel}
                onSend={handleShareSend}
                shareError={shareError}
                shareLoading={shareLoading}
            />
        </>
    );
}

function ShareTicketDialog({
    open,
    onOpenChange,
    ticket,
    recipientEmail,
    onRecipientEmailChange,
    onCancel,
    onSend,
    shareError,
    shareLoading,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="my-0">
                        <Trans>Share Ticket</Trans>
                    </DialogTitle>
                </DialogHeader>
                <div>
                    <p className="mb-4">
                        <Trans>We'll email this ticket to the email address you provide.</Trans>
                    </p>
                    <Input
                        id={`share-email-${ticket.unipass_id}`}
                        autoFocus
                        aria-label={t`Email Address`}
                        type="email"
                        value={recipientEmail}
                        onChange={(event) => onRecipientEmailChange(event.target.value)}
                        placeholder={t`name@example.com`}
                    />
                    {shareError ? (
                        <p className="text-sm mt-2 font-medium text-red-600" role="alert">
                            {shareError}
                        </p>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onCancel} disabled={shareLoading}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        onClick={onSend}
                        className="mb-2 md:mb-0"
                        disabled={shareLoading || !recipientEmail.trim()}
                    >
                        <Trans>Send</Trans>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ViewTicketDialog({
    open,
    onOpenChange,
    ticket,
    onWallet,
    onSend,
    onDownload,
    downloadLoading,
}) {
    const [qrCodeSvg, setQrCodeSvg] = useState('');

    useEffect(() => {
        let active = true;

        QRCode.toString(ticket.unipass_id, {
            type: 'svg',
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF',
            },
            version: 3,
            scale: 6,
            errorCorrectionLevel: 'L',
        })
            .then((svg) => {
                if (active) {
                    setQrCodeSvg(svg);
                }
            })
            .catch(() => {
                if (active) {
                    setQrCodeSvg('');
                }
            });

        return () => {
            active = false;
        };
    }, [ticket.unipass_id]);

    const isScanned = ticket.scanned_status === 1;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-2xl flex-col overflow-y-auto border-solid border-border p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-baseline gap-3">
                            <DialogTitle className="text-2xl mt-0 mb-2 font-bold leading-tight md:text-3xl">
                                {ticket.prod_name}
                            </DialogTitle>
                            <span className="text-xl text-muted-foreground">
                                {formatUnipassNumber(ticket.unipass_number)}
                            </span>
                        </div>
                        {formatAssignedSeat(ticket) ? (
                            <p className="text-sm mt-0 text-muted-foreground">
                                {formatAssignedSeat(ticket)}
                            </p>
                        ) : null}
                        {isScanned ? (
                            <span className="inline-flex rounded-full border border-solid border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                                <Trans>Checked In</Trans>
                            </span>
                        ) : null}
                    </div>
                    <DialogClose asChild>
                        <button
                            type="button"
                            className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={t`Close`}
                        >
                            <X className="h-5 w-5" aria-hidden />
                        </button>
                    </DialogClose>
                </div>

                <div className="flex flex-col items-center gap-4 rounded-2xl sm:p-10">
                    <div
                        className="flex aspect-square w-full max-w-[24rem] items-center justify-center [&_svg]:h-full [&_svg]:w-full bg-white p-8 text-black"
                        aria-label={t`QR code`}
                        dangerouslySetInnerHTML={{ __html: qrCodeSvg }}
                    />
                </div>

                <div className="grid grid-cols-3 gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        className="h-24 flex-col"
                        onClick={onWallet}
                    >
                        <Wallet aria-hidden />
                        <Trans>Wallet</Trans>
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        className="h-24 flex-col"
                        onClick={onSend}
                    >
                        <Send aria-hidden />
                        <Trans>Send</Trans>
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        className="h-24 flex-col"
                        onClick={onDownload}
                        disabled={downloadLoading}
                    >
                        <FileDown aria-hidden />
                        <Trans>PDF</Trans>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AddAllToWalletButton({ appleWalletUrl, googleWalletUrl, onClick }) {
    if (!appleWalletUrl && !googleWalletUrl) return null;

    return (
        <Button type="button" onClick={onClick}>
            <Wallet aria-hidden />
            <Trans>Add All to Wallet</Trans>
        </Button>
    );
}

function WalletDialog({
    open,
    onOpenChange,
    appleWalletUrl,
    googleWalletUrl,
    locale,
    accessToken,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <div className="flex shrink-0 items-center justify-between border-border">
                    <DialogTitle className="mt-0 mb-2 font-bold leading-tight md:text-3xl">
                        <Trans>Add to Wallet</Trans>
                    </DialogTitle>
                    <DialogClose asChild>
                        <button
                            type="button"
                            className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={t`Close`}
                        >
                            <X className="h-5 w-5" aria-hidden />
                        </button>
                    </DialogClose>
                </div>

                <DialogDescription className="text-base">
                    <Trans>
                        Save these tickets to your phone for quick and easy access at the event.
                    </Trans>
                </DialogDescription>

                <div className="flex gap-3 justify-center">
                    <AppleWalletButton
                        appleWalletUrl={appleWalletUrl}
                        locale={locale}
                        accessToken={accessToken}
                    />
                    <GoogleWalletButton
                        googleWalletUrl={googleWalletUrl}
                        locale={locale}
                        accessToken={accessToken}
                    />
                </div>

                <div className="flex items-start gap-3 rounded-xl border border-solid border-border bg-card p-4 text-sm text-muted-foreground">
                    <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <p>
                        <Trans>
                            Wallet passes make it easy to access your tickets quickly at the gate,
                            even without searching through email.
                        </Trans>
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AppleWalletButton({ appleWalletUrl, locale, accessToken }) {
    if (!appleWalletUrl) return null;

    let href = null;

    try {
        href = new URL(appleWalletUrl);
        href.searchParams.set('locale', locale);
        href.searchParams.set('access', accessToken);
    } catch (e) {
        return null;
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 items-center"
        >
            <img
                className="h-10"
                src={locale === 'fr' ? AppleWalletFR : AppleWalletEN}
                alt={t`Add to Apple Wallet`}
            />
        </a>
    );
}

function GoogleWalletButton({ googleWalletUrl, locale, accessToken }) {
    if (!googleWalletUrl) return null;

    let href = null;

    try {
        href = new URL(googleWalletUrl);
        href.searchParams.set('locale', locale);
        href.searchParams.set('access', accessToken);
    } catch (e) {
        return null;
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 items-center"
        >
            <img
                className="h-10"
                src={locale === 'fr' ? GoogleWalletFR : GoogleWalletEN}
                alt={t`Add to Google Wallet`}
            />
        </a>
    );
}

// **** API **** //

function useEventAndCustomerTickets(eventId) {
    const { customerFetch } = useCustomerAuth();
    const [event, setEvent] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        if (!eventId) {
            setStatus('error');
            setError(new Error('Invalid event id.'));
            setEvent(null);
            setTickets([]);
            return;
        }

        let active = true;

        async function fetchEventAndTickets() {
            setStatus('loading');
            setError(null);

            try {
                // *** API Requests *** //
                const headers = {
                    'Content-Type': 'application/json',
                };

                const [eventResponse, ticketsResponse] = await Promise.all([
                    customerFetch(`event/${eventId}`, {
                        headers,
                    }),
                    customerFetch(`event/${eventId}/tickets`, {
                        headers,
                    }),
                ]);

                if (eventResponse.status !== 200 || ticketsResponse.status !== 200) {
                    throw new Error('Unable to load your event and tickets.');
                }

                // *** Parse JSON Response *** //

                const [eventData, ticketsData] = await Promise.all([
                    eventResponse.json(),
                    ticketsResponse.json(),
                ]);

                const event = eventData?.event;
                const tickets = ticketsData?.tickets;

                if (!active) return;

                if (!event || !Array.isArray(tickets)) {
                    throw new Error('Invalid response from the server');
                }

                // *** Set Values *** //

                setEvent(eventData.event);
                setTickets(ticketsData.tickets);
                setStatus('ready');
            } catch (err) {
                if (!active) return;

                setEvent(null);
                setTickets([]);
                setStatus('error');
                setError(err);
            }
        }

        fetchEventAndTickets();

        return () => {
            active = false;
        };
    }, [eventId, customerFetch, reloadToken]);

    return [
        event,
        tickets,
        status,
        error,
        () => setReloadToken((currentReloadToken) => currentReloadToken + 1),
    ];
}

// **** Loading State UI **** //

function LoadingState() {
    return (
        <div className="flex min-h-[calc(100vh-64px)] items-center justify-center px-3">
            <div className="flex flex-col items-center gap-3 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
                <p className="text-sm text-muted-foreground">
                    <Trans>Loading your tickets...</Trans>
                </p>
            </div>
        </div>
    );
}

function AuthenticationFailedState() {
    return (
        <div className="flex min-h-[calc(100vh-64px)] items-center justify-center px-3">
            <div className="flex max-w-xl flex-col items-center gap-3 text-center">
                <h1 className="text-xl font-semibold text-foreground sm:text-2xl">
                    <Trans>Your ticket link could not be verified.</Trans>
                </h1>
                <p className="text-sm text-muted-foreground">
                    <Trans>It may be expired or invalid. Please request a new one.</Trans>
                </p>
            </div>
        </div>
    );
}

function formatUnipassNumber(num, showHash = true) {
    if (showHash) {
        return '#' + String(num).padStart(3, '0');
    } else {
        return String(num).padStart(3, '0');
    }
}

function formatAssignedSeat(seat) {
    let units = [];

    if (seat.section_label) {
        if (seat.section_type === 'section') {
            units.push(t`Sec ${seat.section_label}`);
        } else {
            units.push(seat.section_label);
        }
    }

    if (seat.row_label) {
        if (seat.row_type === 'row') {
            units.push(t`Row ${seat.row_label}`);
        } else if (seat.row_type === 'table') {
            units.push(t`Table ${seat.row_label}`);
        } else {
            units.push(seat.row_label);
        }
    }

    if (seat.seat_label) {
        if (seat.seat_type === 'seat') {
            units.push(t`Seat ${seat.seat_label}`);
        } else if (seat.seat_type === 'ga') {
            // We don't show a type label when its general admission
            units.push(seat.seat_label);
        } else {
            units.push(seat.seat_label);
        }
    }

    return units.join(' | ');
}
